/**
 * Centralized Calendar Status Utilities
 *
 * Single source of truth for:
 * - Status inference logic
 * - Status colors, icons, gradients
 * - Status labels
 */

import { ICONS } from '@/constants/icons';
import { Post, PostVariationSummary, OccurrenceStatus } from '@/services/posts';
import { getPostStatusCounts, getVariationPrimaryStatus } from '@/utils/calendar';

// Valid calendar statuses
// NOTE: this union mixes the publishing axis (MAPPED/SCHEDULED/PUBLISHING/
// PUBLISHED/PARTIAL/FAILED/MISSED/CANCELLED) with workflow states (PENDING/
// APPROVED/CHANGES_REQUESTED) — the exact axis-blending POST-STATUS-MODEL.md
// warns against. Left as-is in Stage 2c-i (not a collapse-logic change); the
// publishing-only `OccurrenceStatus` type (services/posts.ts) + `getStatusBadge`
// below are the first-class, axis-pure alternative for new code.
export type CalendarStatus = 'UNSCHEDULED' | 'MAPPED' | 'SCHEDULED' | 'PUBLISHING' | 'PUBLISHED' | 'PARTIAL' | 'FAILED' | 'MISSED' | 'CANCELLED' | 'PENDING' | 'APPROVED' | 'CHANGES_REQUESTED';

// Status style configuration
export interface StatusStyle {
  color: string;      // Text color class
  bgColor: string;    // Background color class
  icon: string;       // Icon name from ICONS
  gradient: string;   // Gradient for popovers
  headerTextColor: string;  // Text color for popover headers ('text-white' or 'text-gray-900')
}

/**
 * Centralized status styles - SINGLE SOURCE OF TRUTH
 *
 * Colors:
 * - Mapped: black (gray-900)
 * - Scheduled: teal-600
 * - Published: teal-400 (light teal)
 * - Failed: red-600
 */
export const STATUS_STYLES: Record<CalendarStatus, StatusStyle> = {
  UNSCHEDULED: {
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    icon: ICONS.FILE,
    gradient: 'from-gray-400 to-gray-500',
    headerTextColor: 'text-white',
  },
  MAPPED: {
    color: 'text-gray-900',
    bgColor: 'bg-gray-100',
    icon: ICONS.CALENDAR,
    gradient: 'from-secondary-600 to-secondary-700',
    headerTextColor: 'text-white',
  },
  SCHEDULED: {
    color: 'text-teal-600',
    bgColor: 'bg-teal-50',
    icon: ICONS.CLOCK,
    gradient: 'from-teal-500 to-teal-600',
    headerTextColor: 'text-white',
  },
  PUBLISHING: {
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    icon: ICONS.LOADER,
    gradient: 'from-yellow-500 to-orange-500',
    headerTextColor: 'text-white',
  },
  PUBLISHED: {
    color: 'text-teal-400',
    bgColor: 'bg-teal-50',
    icon: ICONS.CHECK,
    gradient: 'from-teal-300 to-teal-400',
    headerTextColor: 'text-gray-900',
  },
  PARTIAL: {
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    icon: ICONS.PARTIAL,
    gradient: 'from-amber-400 to-amber-500',
    headerTextColor: 'text-white',
  },
  FAILED: {
    color: 'text-red-600',
    bgColor: 'bg-red-50',
    icon: ICONS.WARNING,
    gradient: 'from-red-500 to-red-600',
    headerTextColor: 'text-white',
  },
  MISSED: {
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
    icon: ICONS.MISSED,
    gradient: 'from-slate-400 to-slate-500',
    headerTextColor: 'text-white',
  },
  CANCELLED: {
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    icon: ICONS.CROSS,
    gradient: 'from-gray-400 to-gray-500',
    headerTextColor: 'text-white',
  },
  PENDING: {
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    icon: ICONS.CLOCK,
    gradient: 'from-amber-500 to-orange-500',
    headerTextColor: 'text-white',
  },
  APPROVED: {
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    icon: ICONS.CHECK,
    gradient: 'from-green-500 to-emerald-600',
    headerTextColor: 'text-white',
  },
  CHANGES_REQUESTED: {
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
    icon: ICONS.EDIT,
    gradient: 'from-orange-500 to-red-500',
    headerTextColor: 'text-white',
  },
};

/**
 * Get status style for a given status
 */
export const getStatusStyle = (status: string): StatusStyle => {
  return STATUS_STYLES[status as CalendarStatus] || STATUS_STYLES.UNSCHEDULED;
};

/**
 * Get status info with translated label
 */
export const getStatusInfo = (
  status: string,
  t: (key: string) => string
): StatusStyle & { label: string } => {
  const style = getStatusStyle(status);
  return {
    ...style,
    label: t(`status_${status.toLowerCase()}`),
  };
};

/**
 * Infer calendar status from post data
 *
 * Logic:
 * 1. First check `post.displayStatus` (server-resolved latest-occurrence status)
 * 2. Then check the raw `post.status` from the calendar API
 * 3. Then check calendarMappings array
 * 4. Then check instancesSummary
 *
 * Priority (Task #182, matches backend `occurrenceStatusSeverity`):
 * FAILED > PARTIAL > MISSED > PUBLISHING > PUBLISHED > SCHEDULED > MAPPED > CANCELLED
 */
export const getInferredStatus = (post: Post): CalendarStatus => {
  // First check `post.displayStatus` — the server-resolved
  // (`latestOccurrenceStatus(post) ?? occurrenceStatusFromPostChannels(...)`)
  // single-badge collapse, mirroring `getPostStatusBadge`'s displayStatus-first
  // pattern above. `post.status` is the RAW DB column and is deliberately NOT
  // rewritten by the shoot/publish path (see POST-STATUS-MODEL.md) — it can be
  // stale (e.g. still 'SCHEDULED' for a post that has already PUBLISHED), so
  // `displayStatus`, when present, is trusted first (Task #182).
  if (post.displayStatus && isOccurrenceStatus(post.displayStatus)) {
    return post.displayStatus as CalendarStatus;
  }

  // Fall back to `post.status` from the calendar API (calendar API sets
  // status directly, not via calendarMappings array).
  // Stage 2c-ii: widened from the original 4-value subset (MAPPED/SCHEDULED/
  // PUBLISHED/FAILED) to the full first-class occurrence vocabulary —
  // calendar entries (`calendarService.getCalendarPosts`, backed by
  // `resolveOccurrenceStatus` server-side) now populate `post.status` with
  // PUBLISHING/PARTIAL/MISSED/CANCELLED too, and those are equally
  // "definitive" direct signals that should be trusted here (when
  // `displayStatus` is absent) rather than falling through to the
  // Failed-first-folded mapping/summary derivation below. See
  // docs/architecture/POST-STATUS-MODEL.md.
  if (
    post.status &&
    ['MAPPED', 'SCHEDULED', 'PUBLISHING', 'PUBLISHED', 'PARTIAL', 'FAILED', 'MISSED', 'CANCELLED'].includes(post.status)
  ) {
    return post.status as CalendarStatus;
  }

  const mappings = post.calendarMappings || [];
  const summary = post.instancesSummary;

  // Check mapping statuses and instances summary.
  //
  // NOTE: `CalendarMappingStatus` has no PARTIAL/MISSED/PUBLISHING/CANCELLED
  // value (see POST-STATUS-MODEL.md), so `hasPartial`/`hasMissed`/
  // `hasPublishing`/`hasCancelled` can ONLY come from `instancesSummary` —
  // there is no raw-mapping fallback for them, matching every other
  // first-class-bucket helper in this codebase (`isPostFailed`/
  // `getPostStatusCounts` in `utils/calendar.ts`). This branch is rarely
  // reached in practice (the `displayStatus`/`post.status` checks above
  // already cover the common calendar-entry path), but a data source that
  // reaches here with only `calendarMappings` + `instancesSummary` (no
  // direct occurrence-status signal) should still resolve
  // Partial/Missed/Publishing/Cancelled instead of silently falling through
  // to 'UNSCHEDULED'.
  const hasFailed = mappings.some(m => m.status === 'FAILED') || (summary?.failed ?? 0) > 0;
  const hasPartial = (summary?.partial ?? 0) > 0;
  const hasMissed = (summary?.missed ?? 0) > 0;
  const hasPublishing = (summary?.publishing ?? 0) > 0;
  const hasPublished = mappings.some(m => m.status === 'PUBLISHED') || (summary?.published ?? 0) > 0;
  const hasScheduled = mappings.some(m => m.status === 'SCHEDULED') || (summary?.scheduled ?? 0) > 0;
  const hasMapped = mappings.some(m => m.status === 'MAPPED');
  const hasCancelled = (summary?.cancelled ?? 0) > 0;

  // Return highest priority status, ordered to match the backend's
  // canonical `occurrenceStatusSeverity` (backend/src/utils/publish-state.ts):
  // FAILED(7) > PARTIAL(6) > MISSED(5) > PUBLISHING(4) > PUBLISHED(3) >
  // SCHEDULED(2) > MAPPED(1) > CANCELLED(0). Task #182: this fallback
  // previously had no derived PUBLISHING/CANCELLED branch even though
  // `instancesSummary.publishing`/`.cancelled` exist — added here in the
  // correct severity slot so a data source reaching this far can still
  // resolve them instead of silently falling through to 'UNSCHEDULED'
  // (PUBLISHING) or 'MAPPED'/'UNSCHEDULED' (CANCELLED). Also: MISSED
  // previously ranked below MAPPED (and below Published/Scheduled), so a
  // post with both MISSED and MAPPED signals wrongly showed "Mapped"
  // instead of the higher-severity "Missed".
  if (hasFailed) return 'FAILED';
  if (hasPartial) return 'PARTIAL';
  if (hasMissed) return 'MISSED';
  if (hasPublishing) return 'PUBLISHING';
  if (hasPublished) return 'PUBLISHED';
  if (hasScheduled) return 'SCHEDULED';
  if (hasMapped) return 'MAPPED';
  if (hasCancelled) return 'CANCELLED';

  return 'UNSCHEDULED';
};

/**
 * Get status icon and color for compact displays
 */
export const getStatusIcon = (post: Post): { icon: string; color: string; label: string } => {
  const status = getInferredStatus(post);
  const style = getStatusStyle(status);
  return {
    icon: style.icon,
    color: style.color,
    label: status,
  };
};

// ============================================================================
// First-class occurrence status badge (Stage 2c-i)
// ============================================================================

/**
 * Badge presentation (label/color/bgColor/icon) for a single first-class
 * publishing occurrence state — the 8 states named in
 * docs/architecture/POST-STATUS-MODEL.md.
 */
export interface StatusBadge {
  label: string;
  color: string;
  bgColor: string;
  icon: string;
}

/**
 * Badge presentation WITHOUT a label — `label` is always produced from the
 * translated `status_<lowercase>` key at call time (see `getStatusBadge`
 * below), never hardcoded English, so the badge always speaks the active
 * locale (Task #182 i18n regression fix).
 */
type StatusBadgeStyle = Omit<StatusBadge, 'label'>;

/**
 * Single source of truth mapping every `OccurrenceStatus` to its badge
 * color/icon presentation (label is translated separately — see
 * `getStatusBadge`). Reuses the color/icon tokens already defined in
 * `STATUS_STYLES` above, but scoped to ONLY the 8 publishing-axis states
 * (no PENDING/APPROVED/CHANGES_REQUESTED/UNSCHEDULED leakage) — see the
 * axis-blending note on `CalendarStatus`.
 *
 * - PARTIAL translates to the "Partial success" equivalent (never "Failed")
 *   — per the model, calling a mixed-outcome occurrence "Failed" misleads
 *   users into thinking the whole post failed.
 * - MISSED and CANCELLED get their own distinct, muted presentations.
 *
 * NOTE: this helper does NOT decide which occurrence's status to show for a
 * post/variation (e.g. "latest non-cancelled") — that selection happens
 * upstream (backend `displayStatus`, or a future frontend collapse helper).
 * This is purely the color/icon lookup for a status you already have.
 */
export const OCCURRENCE_STATUS_BADGES: Record<OccurrenceStatus, StatusBadgeStyle> = {
  MAPPED: {
    color: STATUS_STYLES.MAPPED.color,
    bgColor: STATUS_STYLES.MAPPED.bgColor,
    icon: STATUS_STYLES.MAPPED.icon,
  },
  SCHEDULED: {
    color: STATUS_STYLES.SCHEDULED.color,
    bgColor: STATUS_STYLES.SCHEDULED.bgColor,
    icon: STATUS_STYLES.SCHEDULED.icon,
  },
  PUBLISHING: {
    color: STATUS_STYLES.PUBLISHING.color,
    bgColor: STATUS_STYLES.PUBLISHING.bgColor,
    icon: STATUS_STYLES.PUBLISHING.icon,
  },
  PUBLISHED: {
    color: STATUS_STYLES.PUBLISHED.color,
    bgColor: STATUS_STYLES.PUBLISHED.bgColor,
    icon: STATUS_STYLES.PUBLISHED.icon,
  },
  PARTIAL: {
    color: STATUS_STYLES.PARTIAL.color,
    bgColor: STATUS_STYLES.PARTIAL.bgColor,
    icon: STATUS_STYLES.PARTIAL.icon,
  },
  FAILED: {
    color: STATUS_STYLES.FAILED.color,
    bgColor: STATUS_STYLES.FAILED.bgColor,
    icon: STATUS_STYLES.FAILED.icon,
  },
  MISSED: {
    color: STATUS_STYLES.MISSED.color,
    bgColor: STATUS_STYLES.MISSED.bgColor,
    icon: STATUS_STYLES.MISSED.icon,
  },
  CANCELLED: {
    color: STATUS_STYLES.CANCELLED.color,
    bgColor: STATUS_STYLES.CANCELLED.bgColor,
    icon: STATUS_STYLES.CANCELLED.icon,
  },
};

/**
 * Get the badge presentation for a first-class occurrence status.
 *
 * `label` is ALWAYS the translated `status_<lowercase>` key (matches the
 * pre-existing `getStatusInfo` convention) — never hardcoded English. Color/
 * bgColor/icon come from `OCCURRENCE_STATUS_BADGES` (unchanged first-class
 * presentation per status).
 *
 * @param status - One of the 8 `OccurrenceStatus` values
 * @param t - Translator (from `useTranslation`), same shape as `getStatusInfo`
 * @returns { label, color, bgColor, icon }
 */
export const getStatusBadge = (
  status: OccurrenceStatus,
  t: (key: string) => string
): StatusBadge => {
  return {
    ...OCCURRENCE_STATUS_BADGES[status],
    // Explicit `common:` namespace (Task #182 R2-5) — immune to caller
    // useTranslation() namespace ordering. Callers like BrandCheck/
    // DraggablePostCard load `['calendar', 'common']`, and `calendar.json`
    // used to carry a stale duplicate `status_partial` ("Partial") that won
    // the lookup before ever reaching the canonical common.json entry
    // ("Partial success"). All 8 status_* keys used here live in
    // common.json — see docs/architecture/POST-STATUS-MODEL.md.
    label: t(`common:status_${status.toLowerCase()}`),
  };
};

/**
 * Type guard: is this raw status string one of the 8 first-class
 * `OccurrenceStatus` values? Used at calendar-occurrence render sites
 * (Stage 2c-ii) to safely route an already-known-accurate `post.status`
 * (e.g. from `calendarService.getCalendarPosts`) through `getStatusBadge`,
 * while still falling back to the legacy `getStatusStyle`/translated-label
 * path for anything outside the occurrence vocabulary (workflow states like
 * PENDING/APPROVED, or UNSCHEDULED) — defensive, should be unreachable for
 * genuine calendar entries.
 */
export const isOccurrenceStatus = (status: string): status is OccurrenceStatus =>
  Object.prototype.hasOwnProperty.call(OCCURRENCE_STATUS_BADGES, status);

// ============================================================================
// Shared calendar-card status-badge helpers (Task #182 fold)
// ============================================================================
//
// `getPostStatusBadge` and `getVariationStatusBadge` below were previously
// copy-pasted verbatim (post: 3 near-identical copies; variation-fallback
// switch: 2 identical copies) across `DraggableGridPostGroup.tsx`,
// `DraggableSidebarPost.tsx`, and `DraggableSidebarPostGroup.tsx`. Extracted
// here as the single source of truth; all three components now import and
// call these instead of defining their own local copy.
//
// NOTE (cosmetic normalization): the pre-fold `DraggableGridPostGroup` copy
// used a slightly different fallback text color for Published/Scheduled
// ('text-teal-700' for both) than the other two copies ('text-teal-400' /
// 'text-teal-600', matching `STATUS_STYLES.PUBLISHED.color` /
// `STATUS_STYLES.SCHEDULED.color` — the same canonical colors the
// `displayStatus`-first branch of this same function already renders via
// `getStatusBadge`). This fold standardizes on the canonical `STATUS_STYLES`
// colors (matching 2 of the 3 pre-fold copies, and matching this function's
// own primary `displayStatus` path), so `DraggableGridPostGroup`'s fallback
// badge is now internally consistent with its own `displayStatus` badge
// instead of silently shifting color when the fallback path is taken.

export interface PostStatusBadge extends StatusBadge {
  count: number;
}

/**
 * Get inferred status badge for a POST based on `displayStatus`, falling
 * back to `calendarMappings`/`instancesSummary` counts when `displayStatus`
 * is absent.
 *
 * Stage 2c-ii: PREFERS the server-computed `post.displayStatus` (latest
 * non-cancelled occurrence — POST-STATUS-MODEL.md) rendered via the
 * first-class `getStatusBadge`, so Partial/Missed/Cancelled show as
 * themselves. Falls back to the legacy Failed > Published > Scheduled >
 * Mapped fold below only when `displayStatus` is absent (defensive).
 */
export const getPostStatusBadge = (
  post: Post,
  t: (key: string, options?: { defaultValue?: string }) => string
): PostStatusBadge | null => {
  // `getPostStatusCounts` SELECTS `instancesSummary` when present (else
  // falls back to the raw mapping tally) — it does NOT sum the two, since
  // `instancesSummary` is server-derived from this same `calendarMappings`
  // array and summing would double-count every mapping.
  const counts = getPostStatusCounts(post);

  if (post.displayStatus) {
    const badge = getStatusBadge(post.displayStatus, t);
    const countKey = post.displayStatus.toLowerCase() as keyof typeof counts;
    return { ...badge, count: counts[countKey] ?? 0 };
  }

  // Priority: Failed > Published > Scheduled > Mapped
  if (counts.failed > 0) {
    return {
      color: STATUS_STYLES.FAILED.color,
      bgColor: STATUS_STYLES.FAILED.bgColor,
      label: t('status_failed'),
      icon: ICONS.WARNING,
      count: counts.failed,
    };
  }
  if (counts.published > 0) {
    return {
      color: STATUS_STYLES.PUBLISHED.color,
      bgColor: STATUS_STYLES.PUBLISHED.bgColor,
      label: t('status_published'),
      icon: ICONS.CHECK,
      count: counts.published,
    };
  }
  if (counts.scheduled > 0) {
    return {
      color: STATUS_STYLES.SCHEDULED.color,
      bgColor: STATUS_STYLES.SCHEDULED.bgColor,
      label: t('status_scheduled'),
      icon: ICONS.CLOCK,
      count: counts.scheduled,
    };
  }
  if (counts.mapped > 0) {
    return {
      color: STATUS_STYLES.MAPPED.color,
      bgColor: STATUS_STYLES.MAPPED.bgColor,
      label: t('status_mapped'),
      icon: ICONS.CALENDAR,
      count: counts.mapped,
    };
  }

  return null; // No calendar status - post not mapped
};

/**
 * Get inferred status badge for a POST VARIATION, based on the variation's
 * own `displayStatus`, falling back to `getVariationPrimaryStatus` (which
 * internally unions `variation.calendarMappings` with
 * `variation.instancesSummary`) when `displayStatus` is absent.
 *
 * Stage 2a follow-up (POST-STATUS-MODEL.md): PREFERS the server-computed
 * `variation.displayStatus` (latest non-cancelled occurrence, computed from
 * the variation's OWN calendarMappings — mirrors `post.displayStatus`
 * handling in `getPostStatusBadge` above) rendered via the first-class
 * `getStatusBadge`, so Partial/Missed/Cancelled show as themselves instead
 * of a blank "Not on calendar" badge. Falls back to `getVariationPrimaryStatus`
 * (client-side fold; Priority: Failed > Published > Scheduled > Mapped — no
 * Partial/Missed branch) only when `displayStatus` is absent (defensive).
 */
export const getVariationStatusBadge = (
  variation: PostVariationSummary,
  t: (key: string) => string
): StatusBadge | null => {
  if (variation.displayStatus) {
    return getStatusBadge(variation.displayStatus, t);
  }

  switch (getVariationPrimaryStatus(variation)) {
    case 'failed':
      return getStatusBadge('FAILED', t);
    case 'published':
      return getStatusBadge('PUBLISHED', t);
    case 'scheduled':
      return getStatusBadge('SCHEDULED', t);
    case 'mapped':
      return getStatusBadge('MAPPED', t);
    default:
      return null;
  }
};
